/**
 * Space atmosphere in post (D-047 lighting overhaul), lit by the environment's own key light (environment/
 * atmosphere.ts), so haze, god rays and shadows always agree:
 *
 * - **Depth fog toward the nebula colours:** beyond `fog.start`, distant geometry fades toward the blurred baked sky
 *   in its own direction (never above `fog.max`), which gives depth and scale against the painted sky.
 * - **Field dust:** an analytic participating medium in a few small patches scattered through the field (pockets
 *   with clear space between them, never one veil over the field). Each patch's density falls off quadratically
 *   from its centre, so the optical depth along the camera ray has a closed form (no marching); one noise octave
 *   breaks the edges into wisps and the opacity is capped. In-scattered light = star × Henyey-Greenstein phase
 *   (forward-bright toward the star) + a sky ambient share, tinted toward the system's nebula hues; the scene behind
 *   is attenuated by the transmittance.
 * - **God rays:** a quarter-resolution radial blur toward the star's screen position of the sky-and-star source
 *   (bright background pixels near the star; geometry occludes them), added to the HDR image.
 *
 * All of it is scene-referred and happens before tone mapping. Per-frame CPU work is allocation-free.
 */
import {
  HalfFloatType,
  Matrix4,
  NodeMaterial,
  NodeUpdateType,
  QuadMesh,
  RenderTarget,
  RendererUtils,
  TempNode,
  Vector2,
  Vector3,
  Vector4,
  type Camera,
  type Node,
  type NodeBuilder,
  type NodeFrame,
  type PerspectiveCamera,
  type Texture,
  type TextureNode,
  type UniformNode,
} from 'three/webgpu'
import {
  Fn,
  Loop,
  context,
  clamp,
  dot,
  exp,
  float,
  getViewPosition,
  int,
  length,
  luminance,
  max,
  min,
  mix,
  mx_noise_float,
  normalize,
  passTexture,
  pmremTexture,
  pow,
  select,
  sqrt,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from 'three/tsl'
import type { AtmosphereState } from '../environment/atmosphere'

type V2 = Node<'vec2'>
type V3 = Node<'vec3'>

// ---------------------------------------------------------------------------------------------------------------
// Pure maths (tested)
// ---------------------------------------------------------------------------------------------------------------

const _dir = new Vector4()
const _clip = new Vector4()

/**
 * Screen position of a direction at infinity (the star): `out` = (u, v, visibility) with u, v in [0, 1] (v down,
 * WebGPU) and visibility 1 on screen, fading to 0 a `margin` (UV units) past the edges and 0 behind the camera.
 * Allocation-free.
 */
export function sunScreenPosition(dir: Vector3, camera: Camera, margin: number, out: Vector3): Vector3 {
  _dir.set(dir.x, dir.y, dir.z, 0).applyMatrix4(camera.matrixWorldInverse)
  _clip.copy(_dir).applyMatrix4(camera.projectionMatrix)
  if (_clip.w <= 1e-6) return out.set(0.5, 0.5, 0)
  const u = (_clip.x / _clip.w) * 0.5 + 0.5
  const v = 1 - ((_clip.y / _clip.w) * 0.5 + 0.5)
  const edge = Math.max(-u, u - 1, -v, v - 1, 0)
  return out.set(u, v, margin > 0 ? Math.max(0, 1 - edge / margin) : edge > 0 ? 0 : 1)
}

/**
 * Optical-depth integral ∫ (1 − |o + t·d − c|² / R²) dt over [a, b] along a unit ray from the origin (the camera):
 * the closed form the dust shader evaluates. `q = −c`, `qd = q·d`, `qq = q·q`.
 */
export function dustIntegral(qq: number, qd: number, r: number, a: number, b: number): number {
  if (b <= a) return 0
  const r2 = r * r
  return b - a - (qq * (b - a) + qd * (b * b - a * a) + (b * b * b - a * a * a) / 3) / r2
}

/** Henyey-Greenstein phase function (1/sr). */
export function henyeyGreenstein(cosTheta: number, g: number): number {
  const g2 = g * g
  return (1 - g2) / (4 * Math.PI * (1 + g2 - 2 * g * cosTheta) ** 1.5)
}

// ---------------------------------------------------------------------------------------------------------------
// Fog and dust (TSL, full resolution, in the composite)
// ---------------------------------------------------------------------------------------------------------------

/** Uniforms bound to the shared atmosphere state and the camera (same objects, so updates flow in place). */
export interface HazeUniforms {
  readonly sunDir: UniformNode<'vec3', Vector3>
  readonly sunColor: UniformNode<'vec3', Vector3>
  readonly dust: UniformNode<'vec4', Vector4>
  readonly dustParams: UniformNode<'vec4', Vector4>
  /** The dust patches (xyz centre render space, w radius; 0 = unused). */
  readonly patches: readonly UniformNode<'vec4', Vector4>[]
  readonly dustShape: UniformNode<'vec4', Vector4>
  readonly dustTint: UniformNode<'vec3', Vector3>
  readonly fog: UniformNode<'vec4', Vector4>
  readonly tint: UniformNode<'vec4', Vector4>
  readonly projInv: UniformNode<'mat4', Matrix4>
  readonly camWorld: UniformNode<'mat4', Matrix4>
}

export function hazeUniforms(atmos: AtmosphereState, camera: PerspectiveCamera): HazeUniforms {
  return {
    sunDir: uniform(atmos.sunDirection),
    // Color is a vec3-compatible object with r, g, b; a Vector3 view is kept in sync by syncHaze().
    sunColor: uniform(new Vector3()),
    dust: uniform(atmos.dust),
    dustParams: uniform(atmos.dustParams),
    patches: atmos.patches.map((v) => uniform(v)),
    dustShape: uniform(atmos.dustShape),
    dustTint: uniform(new Vector3(1, 1, 1)),
    fog: uniform(atmos.fog),
    tint: uniform(atmos.tint),
    projInv: uniform(camera.projectionMatrixInverse),
    camWorld: uniform(camera.matrixWorld),
  }
}

/** Copies the colour into its vec3 uniform (Color has no x/y/z). Allocation-free. */
export function syncHaze(u: HazeUniforms, atmos: AtmosphereState): void {
  const c = atmos.sunColor
  u.sunColor.value.set(c.r, c.g, c.b)
  const t = atmos.dustTint
  u.dustTint.value.set(t.r, t.g, t.b)
}

/** Applies depth fog and field dust to the HDR colour. */
export function applyHaze(hdr: V3, p: V2, depth: TextureNode, u: HazeUniforms, sky: Texture | null): V3 {
  const d = depth.sample(p).r
  const isSky = d.lessThanEqual(0)
  const viewPos = getViewPosition(p, d, u.projInv) as unknown as V3
  const dirView = normalize(getViewPosition(p, float(0.5), u.projInv) as unknown as V3)
  const dirWorld = normalize(u.camWorld.mul(vec4(dirView, 0)).xyz)
  const dist = select(isSky, float(1e7), length(viewPos))

  // Depth fog toward the blurred sky in the pixel's own direction (never on the sky itself).
  const fog = u.fog
  let out: V3 = hdr
  if (sky) {
    const fogColor = (pmremTexture(sky, dirWorld, fog.w) as unknown as V3).mul(u.tint.y)
    const amount = float(1).sub(exp(max(dist.sub(fog.x), 0).mul(fog.y).negate())).mul(fog.z)
    out = mix(out, fogColor, select(isSky, float(0), amount))
  }

  // Field dust: a few small patches (spheres with quadratic density falloff) scattered through the field, so the
  // player flies through pockets of dust with clear space between them. Each patch's optical depth along the ray has
  // a closed form (no marching), normalised by its radius (a 2 km and a 20 km pocket read alike), and one noise
  // octave at the segment midpoint breaks the edge into wisps. Unused patches have radius 0.
  let depthSum: Node<'float'> = float(0)
  for (const patch of u.patches) {
    const c = patch.xyz
    const r = patch.w
    const q = c.negate()
    const qd = dot(q, dirWorld)
    const qq = dot(q, q)
    const disc = qd.mul(qd).sub(qq.sub(r.mul(r)))
    const has = disc.greaterThan(0).and(r.greaterThan(0))
    const root = sqrt(max(disc, 0))
    const a = max(qd.negate().sub(root), 0)
    const b = max(min(qd.negate().add(root), dist), a)
    const r2 = max(r.mul(r), 1e-3)
    const integral = b.sub(a).sub(qq.mul(b.sub(a)).add(qd.mul(b.mul(b).sub(a.mul(a)))).add(b.mul(b).mul(b).sub(a.mul(a).mul(a)).div(3)).div(r2))
    // Wisps: noise in patch-local units (stable under the floating origin), scaled per patch radius.
    const mid = dirWorld.mul(a.add(b).mul(0.5)).sub(c).div(max(r, 1e-3)).mul(u.dustShape.x)
    const wisp = clamp(mx_noise_float(mid).mul(u.dustShape.y).add(1), 0, 2)
    depthSum = depthSum.add(select(has, max(integral, 0).div(max(r, 1e-3)).mul(wisp), float(0)))
  }
  const opacity = min(float(1).sub(exp(depthSum.mul(u.dustParams.x).negate())), u.dustParams.w)
  const transmittance = float(1).sub(opacity)
  // Star light scattered toward the camera (HG phase, forward-bright) plus a sky ambient share.
  const g = u.dustParams.y
  const cosT = dot(dirWorld, u.sunDir)
  const g2 = g.mul(g)
  // Dual lobe: a forward Henyey-Greenstein peak toward the star plus an isotropic share (dust reads from any side).
  const forward = float(1).sub(g2).div(pow(float(1).add(g2).sub(g.mul(2).mul(cosT)), 1.5).mul(4 * Math.PI))
  const phase = mix(forward, float(1 / (4 * Math.PI)), u.tint.z)
  const ambient = sky ? (pmremTexture(sky, dirWorld, float(1)) as unknown as V3).mul(u.tint.x) : vec3(0.02).mul(u.tint.x)
  const light = u.sunColor.mul(phase).add(ambient).mul(u.dustParams.z).mul(u.dustTint)
  return out.mul(transmittance).add(light.mul(float(1).sub(transmittance)))
}

// ---------------------------------------------------------------------------------------------------------------
// God rays (two quarter-resolution passes: a smooth source mask, then a radial blur toward the star)
// ---------------------------------------------------------------------------------------------------------------

export interface GodRayUniforms {
  /** xy star UV, z visibility. */
  readonly sun: UniformNode<'vec3', Vector3>
  readonly samples: UniformNode<'float', number>
  readonly decay: UniformNode<'float', number>
  /** Longest ray as a fraction of the screen (UV units). */
  readonly length: UniformNode<'float', number>
  /** Source falloff around the star (1 / UV squared). */
  readonly falloff: UniformNode<'float', number>
  /** HDR luminance above which background pixels feed the rays (the star disc and glare, not the nebula). */
  readonly threshold: UniformNode<'float', number>
  /** Source clamp (keeps the disc from swamping the blur). */
  readonly clampMax: UniformNode<'float', number>
  /** Synthetic corona behind the star (with a depth sky mask only): what occluders carve shafts from. */
  readonly corona: UniformNode<'float', number>
  readonly aspect: UniformNode<'float', number>
}

type RendererState = ReturnType<typeof RendererUtils.resetRendererState>

/**
 * God rays: pass 1 box-filters the thresholded, sky-only, star-weighted HDR colour into a quarter-resolution mask
 * (a smooth source, so the blur has no sampling dots); pass 2 blurs the mask radially toward the star with decaying
 * weights (GPU Gems 3, ch. 13). Occluders (ships, rocks, the derelict) are dark in the mask and cast shafts. The
 * loop count is a uniform, so presets pay only for their samples. Without a depth texture (Low) the sky test is
 * skipped and occlusion comes from the colour alone.
 */
export class GodRayNode extends TempNode<'vec4'> {
  readonly uniforms: GodRayUniforms
  private readonly color: TextureNode
  private readonly depth: TextureNode | null
  private readonly maskTarget = new RenderTarget(1, 1, { depthBuffer: false, type: HalfFloatType })
  private readonly target = new RenderTarget(1, 1, { depthBuffer: false, type: HalfFloatType })
  private readonly quad = new QuadMesh(new NodeMaterial())
  private readonly maskMaterial = new NodeMaterial()
  private readonly blurMaterial = new NodeMaterial()
  private readonly size = new Vector2()
  /** Full-resolution texel (the mask's box taps). */
  private readonly texel = uniform(new Vector2(1, 1))
  private readonly output: TextureNode
  private state: RendererState | undefined = undefined
  scale = 0.25
  enabled = true

  constructor(color: TextureNode, depth: TextureNode | null, uniforms: GodRayUniforms) {
    super('vec4')
    this.color = color
    this.depth = depth
    this.uniforms = uniforms
    this.maskTarget.texture.name = 'post.godRayMask'
    this.target.texture.name = 'post.godRays'
    this.quad.name = 'GodRays'
    this.output = passTexture(this as unknown as Parameters<typeof passTexture>[0], this.target.texture)
    this.updateBeforeType = NodeUpdateType.FRAME
  }

  getTextureNode(): TextureNode {
    return this.output
  }

  /** The source mask (diagnostics). */
  getMaskNode(): TextureNode {
    return passTexture(this as unknown as Parameters<typeof passTexture>[0], this.maskTarget.texture)
  }

  override updateBefore(frame: NodeFrame): undefined {
    const renderer = frame.renderer
    if (!renderer || !this.enabled || this.uniforms.sun.value.z <= 0) return undefined
    this.state = RendererUtils.resetRendererState(renderer, this.state as RendererState)
    renderer.getDrawingBufferSize(this.size)
    const w = Math.max(1, Math.round(this.size.x * this.scale))
    const h = Math.max(1, Math.round(this.size.y * this.scale))
    if (w !== this.target.width || h !== this.target.height) {
      this.maskTarget.setSize(w, h)
      this.target.setSize(w, h)
    }
    this.texel.value.set(1 / Math.max(this.size.x, 1), 1 / Math.max(this.size.y, 1))
    renderer.setRenderTarget(this.maskTarget)
    this.quad.material = this.maskMaterial
    this.quad.render(renderer)
    renderer.setRenderTarget(this.target)
    this.quad.material = this.blurMaterial
    this.quad.render(renderer)
    RendererUtils.restoreRendererState(renderer, this.state)
    return undefined
  }

  override setup(builder: NodeBuilder): Node {
    const shared = context((builder as NodeBuilder & { getSharedContext(): Record<string, unknown> }).getSharedContext())
    const u = this.uniforms
    const color = this.color
    const depth = this.depth
    const texel = this.texel
    const spread = 0.5 / this.scale

    // Pass 1: the source. Four bilinear taps cover the block of full-res pixels under each mask texel.
    const maskFragment = Fn(() => {
      const p = uv()
      const r = length(p.sub(u.sun.xy).mul(vec2(u.aspect, 1)))
      const weight = exp(r.mul(r).mul(u.falloff).negate())
      const acc = vec3(0).toVar()
      const offs: [number, number][] = [
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ]
      for (const [ox, oy] of offs) {
        const at = p.add(texel.mul(vec2(ox, oy)).mul(spread))
        const c = color.sample(at).rgb
        const l = luminance(c)
        const bright = c.mul(max(l.sub(u.threshold), 0).div(max(l, 1e-4)))
        const skyMask = depth ? select(depth.sample(at).r.lessThanEqual(0), float(1), float(0)) : float(1)
        // With depth, the background behind the star is a corona of light that geometry occludes; the bright disc
        // and glare add on top. Without depth (Low) only the bright pixels feed the rays.
        const corona = depth ? vec3(u.corona) : vec3(0)
        acc.addAssign(min(bright, vec3(u.clampMax)).add(corona).mul(skyMask))
      }
      return vec4(acc.mul(0.25).mul(weight), 1)
    })()

    // Pass 2: radial blur of the mask toward the star.
    const mask = texture(this.maskTarget.texture)
    const blurFragment = Fn(() => {
      const p = uv()
      const sunUv = u.sun.xy
      const toSun = sunUv.sub(p)
      const len = length(toSun.mul(vec2(u.aspect, 1)))
      // March all the way to the star (the smooth mask needs no jitter); shafts fade smoothly with distance.
      const step = toSun.div(u.samples)
      const acc = vec3(0).toVar()
      const weight = float(1).toVar()
      Loop({ start: int(0), end: int(u.samples), type: 'int', condition: '<' }, ({ i }) => {
        const at = clamp(p.add(step.mul(float(i).add(0.5))), vec2(0), vec2(1))
        acc.addAssign(mask.sample(at).rgb.mul(weight))
        weight.mulAssign(u.decay)
      })
      const fade = exp(len.mul(len).div(max(u.length.mul(u.length), 1e-4)).negate())
      return vec4(acc.div(max(u.samples, 1)).mul(fade), 1)
    })()

    const passes: [NodeMaterial, string, Node][] = [
      [this.maskMaterial, 'GodRays_mask', maskFragment],
      [this.blurMaterial, 'GodRays_blur', blurFragment],
    ]
    for (const [m, name, fragment] of passes) {
      m.name = name
      m.contextNode = shared
      m.fragmentNode = fragment
      m.needsUpdate = true
    }
    return this.output
  }

  override dispose(): void {
    super.dispose()
    this.maskTarget.dispose()
    this.target.dispose()
    this.maskMaterial.dispose()
    this.blurMaterial.dispose()
  }
}

/** Luminance helper for the rays' tint (kept here so the composite reads cleanly). */
export function rayTint(rays: V3, sunColor: V3): V3 {
  return rays.mul(sunColor.div(max(luminance(sunColor), 1e-3)))
}
